import ApiService from './api.service';
import BackgroundService from './bg.service';

export type Source = {
  id: string;
  name: string;
  type: string;
  description?: string;
  connector_name?: string;
  config?: Record<string, any>;
  secureConfig?: any;
  last_loaded?: string;
  is_default?: boolean;
  fivetran?: {
    connector?: {
      id?: string;
      status?: {
        sync_state?: string;
        setup_state?: string;
      };
      schema_name?: string;
      paused?: boolean;
    };
  };
  sync_info?: {
    status?: string;
    last_sync_completed_at?: string;
    sync_schedule?: {
      enabled?: boolean;
      cron_expression?: string;
    };
  };
  oauth_last_authorized?: string;
  icon_override?: string;
  table_node_prefix?: string;
  draft_shape?: {
    total_tables?: number;
    total_source_records?: number;
  };
  prod_shape?: {
    total_tables?: number;
    total_source_records?: number;
  };
};

export type ConfigField = {
  name: string;
  label: string;
  field_type: 'SHORT_TEXT' | 'LONG_TEXT' | 'NUMBER' | 'JSON' | 'SINGLE_SELECT' | 'MULTI_SELECT' | 'MELTANO_STREAMS' | 'OPTIONS_MULTISELECT';
  choices?: string[];
  description?: string;
  default_value?: string;
  env_var?: string;
  allow_multiple?: boolean;
};

export type ConfigFieldOption = {
  id: string;
  label: string;
  sublabel?: string;
};

export type SourceTypeConfig = {
  id: string;
  title: string;
  provider: string;
  description: string;
  icon_path: string;
  oauth?: any;
  visibility: 'public' | 'legacy' | 'private';
  supports_catalog_discovery?: boolean;
  additional_config?: ConfigField[];
  additional_secret_config?: ConfigField[];
  advanced_config?: ConfigField[];
  exposed_record_types?: any[];
};

export type SourceType = {
  id: string;
  title: string;
  provider: string;
  description: string;
  icon_path: string;
  oauth: boolean;
  visibility: 'public' | 'legacy' | 'private';
};

export type DataLoad = {
  id: string;
  source_id: string;
  status: string;
  full_refresh: boolean;
  drop_schema_first: boolean;
  discover_only?: boolean;
  queued_at?: string;
  started_at?: string;
  // when syncing actually began (excludes queued time); prefer this for elapsed
  sync_started_at?: string;
  completed_at?: string;
  last_progress_at?: string;
  total_records?: number;
  error_message?: string;
  // rows pulled from the source so far, per stream (climbs during extraction,
  // before anything is loaded); only from taps that emit record_count_extracted
  stream_extracted_counts?: Record<string, number>;
  // rows loaded to the warehouse so far, per stream (from the loader)
  stream_load_counts: Record<string, number>;
  // rows this run is expected to move, per stream (denominator); OPTIONAL — only
  // taps that emit a record_count_expected estimate. Absent → no bar, numbers only.
  stream_total_counts?: Record<string, number>;
  error_messages: string[];
};

export type CatalogColumn = {
  name: string;
  type: string;
};

export type CatalogStream = {
  stream: string;
  columns: CatalogColumn[];
  primary_keys: string[];
  replication_keys: string[];
  replication_method: string;
  // approximate row count recorded at discovery, if the tap supports it
  row_count?: number | null;
};

export type CatalogResponse = {
  streams: CatalogStream[] | null;
  has_catalog: boolean;
  active_discovery_job_id: string | null;
};

// Categorize source types for UI display
export type SourceCategory = 'flat_file' | 'snowflake' | 'managed_integration';

export function getSourceCategory(sourceType: string): SourceCategory {
  const type = sourceType?.toLowerCase();
  if (type === 'flat_file') {
    return 'flat_file';
  }
  if (type === 'snowflake' || type === 'snowflake_table') {
    return 'snowflake';
  }
  return 'managed_integration';
}

export function getSourceCategoryLabel(category: SourceCategory): string {
  switch (category) {
    case 'flat_file':
      return 'Flat File';
    case 'snowflake':
      return 'Snowflake Table';
    case 'managed_integration':
      return 'Managed Integration';
  }
}

export type SingleRecordResponse<T> = {
  record: T;
};

export type ListRecordsResponse<T> = {
  records: T[];
  total_records?: number;
  total_pages?: number;
  page?: number;
};

export type SecureConfigLinkField = {
  name: string;
  label: string;
  field_type: string;
  description?: string;
  is_secure: boolean;
};

export type SecureConfigLinkDetails = {
  source_name: string;
  icon_path: string;
  fields: SecureConfigLinkField[];
  branding: {
    logo_path: string | null;
    page_title: string | null;
  };
};

export type FlatFileUploadLinkDetails = {
  source_name: string;
  title: string;
  description: string;
  allow_multiple_uploads: boolean;
  ask_for_description: boolean;
  branding: {
    logo_path: string | null;
    page_title: string | null;
  };
};

export type ShareableAuthLinkDetails = {
  source_name: string;
  connector_title: string;
  connector_type: string;
  icon_path: string;
  redirect_url: string;
  branding: {
    logo_path: string | null;
    page_title: string | null;
    favicon_path: string | null;
  };
};

export class SourceService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
  }): Promise<ListRecordsResponse<Source>> {
    const {
      page = 1,
      pageSize = 50,
      filters = {},
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
      ...filters,
    };

    const result = await ApiService.getInstance().request(
      'GET',
      '/sources',
      queryParams
    ) as ListRecordsResponse<Source>;

    return result;
  }

  async findById(id: string): Promise<Source> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/${id}`
    ) as SingleRecordResponse<Source>;
    return result.record;
  }

  async create(source: Partial<Source>): Promise<Source> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/sources',
      source
    ) as SingleRecordResponse<Source>;
    return result.record;
  }

  async save(source: Partial<Source>): Promise<Source> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/sources/${source.id}`,
      source
    ) as SingleRecordResponse<Source>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/sources/${id}`
    );
  }

  async saveSourceIcon(sourceId: string, fileId: string): Promise<Source> {
    const result = await ApiService.getInstance().request(
      'PUT',
      `/sources/${sourceId}/icon/${fileId}`
    ) as SingleRecordResponse<Source>;
    return result.record;
  }

  async removeSourceIcon(sourceId: string): Promise<Source> {
    const result = await ApiService.getInstance().request(
      'DELETE',
      `/sources/${sourceId}/icon`
    ) as SingleRecordResponse<Source>;
    return result.record;
  }

  async assignNodes(sourceId: string, nodeIds: string[]): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/assign-nodes`,
      { node_ids: nodeIds }
    );
  }

  async getSourceTypes(): Promise<SourceType[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/source-types`
    ) as ListRecordsResponse<SourceType>;
    return result.records;
  }

  async getSourceTypeConfig(sourceType: string): Promise<SourceTypeConfig> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/source-type/${sourceType}/config`
    ) as SingleRecordResponse<SourceTypeConfig>;
    return result.record;
  }

  async initSource(sourceId: string, data: {
    source_record_types?: any[];
    config?: Record<string, any>;
    secure_config?: Record<string, any> | null;
    sync_info?: { sync_schedule?: { cron_expression?: string } };
  }): Promise<{ job_id: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/init`,
      data
    ) as { job_id: string };
    return result;
  }

  async triggerSync(sourceId: string, options?: {
    full_refresh?: boolean;
    drop_schema_first?: boolean;
    selected_streams?: string[] | null;
  }): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/trigger-sync`,
      options || {}
    );
  }

  async enableSync(sourceId: string, paused: boolean): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/enable-sync`,
      { paused }
    );
  }

  async getAuthorizationUrl(sourceId: string): Promise<string> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/connectors/source/${sourceId}/authorization-url`
    ) as SingleRecordResponse<{ authorization_url: string }>;
    return result.record.authorization_url;
  }

  async createShareableAuthUrl(sourceId: string): Promise<string> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/connectors/source/${sourceId}/shareable-authorization-url`
    ) as SingleRecordResponse<{ shareable_auth_url: string }>;
    return result.record.shareable_auth_url;
  }

  async killSync(sourceId: string, dataloadId: string): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/kill-sync/${dataloadId}`
    );
  }

  async getDataLoads(sourceId: string): Promise<DataLoad[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/dataloads',
      { filter: JSON.stringify({ source_id: sourceId }), sort: '-started_at', limit: 20 }
    ) as ListRecordsResponse<DataLoad>;
    return result.records;
  }

  async getStreams(sourceId: string): Promise<string[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/${sourceId}/streams`
    ) as { streams: string[] };
    return result.streams;
  }

  async getCatalog(sourceId: string): Promise<CatalogResponse> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/${sourceId}/catalog`
    ) as CatalogResponse;
    return result;
  }

  async getConfigFieldOptions(sourceId: string, fieldName: string): Promise<ConfigFieldOption[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/${sourceId}/config-options/${fieldName}`
    ) as { options: ConfigFieldOption[] };
    return result.options ?? [];
  }

  async discoverCatalog(sourceId: string): Promise<{ job_id: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/discover`
    ) as { job_id: string };
    return result;
  }

  async createSourceNodes(sourceId: string): Promise<{ job_id: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/create-source-nodes`
    ) as { job_id: string };
    return result;
  }

  async saveCatalogSelections(sourceId: string, meltanoStreams: Record<string, any>): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/save-catalog-selections`,
      { meltano_streams: meltanoStreams }
    );
  }

  async suggestColumns(sourceId: string, payload: {
    stream: string;
    columns: { name: string; type: string }[];
    primary_keys: string[];
    other_selected_streams: { stream: string; columns: string[]; primary_keys: string[] }[];
  }): Promise<{ suggested_columns: string[] }> {
    const response = await ApiService.getInstance().request(
      'POST',
      `/sources/${sourceId}/suggest-columns`,
      payload
    ) as { job_id: string };

    const result = await BackgroundService.getInstance().waitForJob(response.job_id, true);
    return result as { suggested_columns: string[] };
  }
  async createSecureConfigLink(sourceId: string, requestedFields: string[]): Promise<string> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/connectors/source/${sourceId}/secure-config-link`,
      { requested_fields: requestedFields }
    ) as SingleRecordResponse<{ link_url: string }>;
    return result.record.link_url;
  }

  async getSecureConfigLink(code: string): Promise<SecureConfigLinkDetails> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/secure-config-link/${code}`
    ) as SingleRecordResponse<SecureConfigLinkDetails>;
    return result.record;
  }

  async submitSecureConfigLink(code: string, values: Record<string, string>): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/sources/secure-config-link/${code}/submit`,
      { values }
    );
  }

  async createFlatFileUploadLink(
    sourceId: string,
    title: string,
    description: string,
    allowMultipleUploads: boolean,
    askForDescription: boolean = true,
    pipelineNodeId?: string,
    expiresInDays: number = 30,
  ): Promise<string> {
    const body: Record<string, any> = {
      title,
      description,
      allow_multiple_uploads: allowMultipleUploads,
      ask_for_description: askForDescription,
      expires_in_days: expiresInDays,
    };
    if (pipelineNodeId) body.pipeline_node_id = pipelineNodeId;
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/connectors/source/${sourceId}/flat-file-upload-link`,
      body
    ) as SingleRecordResponse<{ link_url: string }>;
    return result.record.link_url;
  }

  async getShareableAuthLink(code: string): Promise<ShareableAuthLinkDetails> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/auth-link/${code}/details`
    ) as SingleRecordResponse<ShareableAuthLinkDetails>;
    return result.record;
  }

  async getShareableLinkBranding(code: string): Promise<{ branding: { logo_path: string | null; page_title: string | null } }> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/auth-link/${code}/branding`
    ) as SingleRecordResponse<{ branding: { logo_path: string | null; page_title: string | null } }>;
    return result.record;
  }

  async getFlatFileUploadLink(code: string): Promise<FlatFileUploadLinkDetails> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/flat-file-upload-link/${code}`
    ) as SingleRecordResponse<FlatFileUploadLinkDetails>;
    return result.record;
  }

  async initFlatFileUpload(
    code: string,
    name: string,
    size: number,
    mimeType: string
  ): Promise<{ record: any; signed_upload_url: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/flat-file-upload-link/${code}/init-upload`,
      { name, size, mime_type: mimeType }
    ) as { record: any; signed_upload_url: string };
    return result;
  }

  async processFlatFileUpload(code: string, fileId: string): Promise<{ job_id: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/flat-file-upload-link/${code}/process`,
      { file_id: fileId }
    ) as { job_id: string };
    return result;
  }

  async describeFlatFileUpload(code: string, fileId: string, description: string): Promise<{ ok: boolean }> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/sources/flat-file-upload-link/${code}/describe`,
      { file_id: fileId, description }
    ) as { ok: boolean };
    return result;
  }

  async getFlatFileUploadJobStatus(
    code: string,
    jobId: string
  ): Promise<{ job_status: string; error_message: string | null }> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/sources/flat-file-upload-link/${code}/job-status/${jobId}`
    ) as { ok: boolean; job_status: string; error_message: string | null };
    return result;
  }
}

export const sourceService = new SourceService();
